import React from 'react';
import PropTypes from 'prop-types';
import { addQuery, QueryVal } from 'Helpers/Query';

const ManagedAccountTabStateContext = React.createContext(undefined);
const ManagedAccountTabDispatchContext = React.createContext(undefined);

export const SET_SELECTED_TAB = 'SET_SELECTED_TAB';

export const BALANCE_ALLOCATION_TAB = 'balance-allocation-tab';
export const BALANCE_ALLOCATION_TAB_LABEL = 'Balance & Allocation';

export const ACTIVITY_SUMMARY_TAB = 'activity-summary-tab';
export const ACTIVITY_SUMMARY_TAB_LABEL = 'Activity Summary';

export const WITHDRAWALS_TRANSFERS_TAB = 'withdrawals-transfers-tab';
export const WITHDRAWALS_TRANSFERS_TAB_LABEL = 'Contributions & Withdrawals';

export const STATEMENT_TAX_TAB = 'statements-tax-docs-tab';
export const STATEMENT_TAX_TAB_LABEL = 'Statements & Tax Docs';

export const ACCOUNT_INFO_TAB = 'account-info-tab';
export const ACCOUNT_INFO_TAB_LABEL = 'Account Info';

const TAB_QP = 'tab';

const defaultState = {
  selectedTab: BALANCE_ALLOCATION_TAB,
};

/**
 * Check if string is an accepted managed account tab type
 * @param tab query parameter
 * @returns {boolean}
 */
function isValidManagedAccountTabType(tab) {
  const tabs = [BALANCE_ALLOCATION_TAB, WITHDRAWALS_TRANSFERS_TAB, STATEMENT_TAX_TAB, ACCOUNT_INFO_TAB];
  return tabs.includes(tab);
}

function init() {
  // set a valid active tab from the query params
  const tab = QueryVal('tab');

  // return active tab if valid
  if (isValidManagedAccountTabType(tab)) {
    return {
      selectedTab: tab,
    };
  }

  // return default tab if valid active tab not found
  return defaultState;
}

function reducer(state, action) {
  switch (action.type) {
    case SET_SELECTED_TAB: {
      const { selectedTab } = action;
      return {
        ...state,
        selectedTab,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

const changeTab = (dispatch, value) => {
  dispatch({ type: SET_SELECTED_TAB, selectedTab: value });
  addQuery({ [TAB_QP]: value });
};

function ManagedAccountTabProvider({ children, initialState }) {
  const [state, dispatch] = React.useReducer(reducer, initialState, init);

  return (
    <ManagedAccountTabStateContext.Provider value={state}>
      <ManagedAccountTabDispatchContext.Provider value={dispatch}>
        {children}
      </ManagedAccountTabDispatchContext.Provider>
    </ManagedAccountTabStateContext.Provider>
  );
}

function useManagedAccountTabStateContext() {
  const context = React.useContext(ManagedAccountTabStateContext);

  if (context === undefined) {
    throw new Error('useManagedAccountTabStateContext must be used within a ManagedAccountTabProvider');
  }

  return context;
}

function useManagedAccountTabDispatchContext() {
  const context = React.useContext(ManagedAccountTabDispatchContext);

  if (context === undefined) {
    throw new Error('useManagedAccountTabDispatchContext must be used within a ManagedAccountTabProvider');
  }

  return context;
}

const useManagedAccountTabContext = () => {
  const dispatch = React.useContext(ManagedAccountTabDispatchContext);
  const state = React.useContext(ManagedAccountTabStateContext);

  if (!dispatch) {
    throw new Error('useManagedAccountTabContext must be called from ManagedAccountTabProvider');
  }

  if (!state) {
    throw new Error('useManagedAccountTabContext must be called from ManagedAccountTabProvider');
  }

  return { dispatch, state };
};

ManagedAccountTabProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  initialState: PropTypes.number.isRequired, // Update this to match your initial state. Likely some object.
};

export {
  ManagedAccountTabProvider,
  useManagedAccountTabContext,
  useManagedAccountTabStateContext,
  useManagedAccountTabDispatchContext,
  changeTab,
  isValidManagedAccountTabType,
};
